import { Dialog } from '@base-ui-components/react/dialog'
import type { FC, HTMLAttributes } from 'react'
import backdropStyles from './backdrop.module.css'
import styles from './dialog.module.css'

const maxWidthMap = {
  xs: styles.maxWidthXs,
  sm: styles.maxWidthSm,
  md: styles.maxWidthMd,
  lg: styles.maxWidthLg,
  xl: styles.maxWidthXl,
} as const

export interface TunaDialogProps {
  open: boolean
  onClose?: () => void
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  fullScreen?: boolean
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
}

export const TunaDialog: FC<TunaDialogProps> = ({
  open,
  onClose,
  maxWidth = 'sm',
  fullWidth,
  fullScreen,
  className,
  children,
  style,
}) => {
  const dialogClasses = [
    styles.dialog,
    fullScreen ? styles.fullScreen : undefined,
    maxWidth === false ? styles.maxWidthNone : maxWidth && !fullScreen ? maxWidthMap[maxWidth] : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Dialog.Root
      open={open}
      onOpenChange={o => {
        if (!o) {
          onClose?.()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={backdropStyles.backdrop} />
        <Dialog.Popup className={dialogClasses} style={fullWidth ? { width: '100%', ...style } : style}>
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export interface DialogTitleProps extends HTMLAttributes<HTMLHeadingElement> {}

export const DialogTitle: FC<DialogTitleProps> = ({ className, ...rest }) => (
  <h2 className={[styles.title, className].filter(Boolean).join(' ')} {...rest} />
)

export interface DialogContentProps extends HTMLAttributes<HTMLDivElement> {
  dividers?: boolean
}

export const DialogContent: FC<DialogContentProps> = ({ dividers, className, style, ...rest }) => (
  <div
    className={[styles.content, className].filter(Boolean).join(' ')}
    style={
      dividers
        ? {
            borderTop: '1px solid var(--tuna-palette-divider)',
            borderBottom: '1px solid var(--tuna-palette-divider)',
            ...style,
          }
        : style
    }
    {...rest}
  />
)

export interface DialogActionsProps extends HTMLAttributes<HTMLDivElement> {}

export const DialogActions: FC<DialogActionsProps> = ({ className, ...rest }) => (
  <div className={[styles.actions, className].filter(Boolean).join(' ')} {...rest} />
)
